<template>
    <div
        class="event-availability"
        :class="'event-availability--' + availabilityStatusSlug"
        v-if="availabilityStatus"
        v-cloak
    >
        Availability:
        {{ availabilityStatus }}
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { slugify } from '../../utils/text';


/**
 * Defines capacity status thresholds for ticket availability.
 * The keys represent the percentage of tickets available, and the values are the corresponding status labels.
 * Be sure to keep the keys in ascending order to ensure correct status assignment.
 *
 * If `availability` is configurable via Basker custom attributes then make sure they match
 */
const CAPACITY_STATUS = {
    0: 'Sold Out',                  // No Availability
    0.1: 'Low Availability',        // Less than 10% of tickets available
    0.2: 'Limited Availability',    // Less than 20% of tickets available
    default: 'Available'          // over 20% of tickets available
};

const BASKER_BOOKING_STATUS_MAP = {
    'onSale': 'On Sale',
    'comingSoon': 'Coming Soon',
    'waitlist': 'Waitlist',
    'offSale': 'Off Sale',
    'cancelled': 'Cancelled',
    'soldOut': 'Sold Out',
};

const props = defineProps({
    event: {
        type: Object,
        required: true,
    },
});

const availabilityStatus = computed(() => {
    if (props.event.bookingStatus) {
        // Manually set availability via CMS admin
        return BASKER_BOOKING_STATUS_MAP[props.event.bookingStatus];
    } else if (Object.keys(props.event.tessitura).length) {
        // Calculate availability status (based on venue capacity)
        // Don't calculate if not Tessitura event
        if (!props.event.tessitura.availability) {
            return CAPACITY_STATUS[0];
        } else {
            const capacity = parseInt(props.event.event.venue?.attributes?.custom.capacity, 10);
            if (capacity) {
                const availability = parseInt(props.event.tessitura.availability, 10);
                const availabilityPercent = availability / capacity;

                const status = Object.keys(CAPACITY_STATUS).find(key => availabilityPercent <= parseFloat(key)) || 'default';
                return CAPACITY_STATUS[status];
            }
        }
    }
});

const availabilityStatusSlug = computed(() => {
    return slugify(availabilityStatus.value);
});

</script>
